// @flow
import { useRef, useEffect } from 'react';
import { getGenesisCDN } from 'shared/lib/clientSettings';
import isGenesis from 'shared/lib/isGenesis';

type Props = {
  region: string,
  playlistSource: string,
  onUpdate: func
};

export default function PlaylistUpdater(props: Props) {
  const {
    region,
    path = '',
    refreshInterval = 0, // seconds
    onUpdate
  } = props;

  const lastPlaylistModifiedTime = useRef(new Date(1970, 1, 1));

  useEffect(() => {
    const retrievePlayList = async () => {
      if (!path) {
        return;
      }

      const playlistPath = isGenesis ? getPlaylistDraftPath(path) : getPlaylistPath();

      try {
        const res = await fetch(`${getGenesisCDN(region)}/${playlistPath}?v=${Date.now()}`);
        const content = await res.json();

        const modifiedTime = new Date(res.headers.get('Last-Modified'));
        if (modifiedTime <= lastPlaylistModifiedTime.current) {
          return;
        }

        lastPlaylistModifiedTime.current = modifiedTime;

        if (content?.data) {
          onUpdate(content.data);
        }
      } catch (error) {
        console.error('Failed to retrieve playlist', error);
      }
    }

    // ---------------start polling--------------------------
    let intervalId;
    // first retrieve
    retrievePlayList();

    if (refreshInterval > 0) {
      intervalId = setInterval(() => {
        retrievePlayList();
      }, refreshInterval * 1000);
    }

    return () => clearInterval(intervalId);
  }, [path, refreshInterval, region, onUpdate]);

  return null;
}

export const PLAYLIST_JSON = 'playlist.json';
export const PLAYLIST_DRAFT_JSON = 'playlist_draft.json';
export const OVERLAY_SETTINGS_JSON = 'overlay_settings.json';
export const OVERLAY_SETTINGS_DRAFT_JSON = 'overlay_settings_draft.json';

export function getAssetFolderPath(path) {
  if (!path) return `assets/livestream-widgets/`;
  // remove special characters, numbers, and spaces
  const snakeCaseAlphanumericPath = path.replace(/[^a-zA-Z0-9-]/g, '');

  // assets/livestream-widgets/new-path/
  return `assets/livestream-widgets/${snakeCaseAlphanumericPath}/`;
}

export function getPlaylistPath(path) {
  // assets/livestream-widgets/new-path/playlist.json
  return `${getAssetFolderPath(path)}${PLAYLIST_JSON}`
}

export function getPlaylistDraftPath(path) {
  return `${getAssetFolderPath(path)}${PLAYLIST_DRAFT_JSON}`
}

export function getOverlaySettingsPath(path) {
  return `${getAssetFolderPath(path)}${OVERLAY_SETTINGS_JSON}`
}

export function getOverlaySettingsDraftPath(path) {
  return `${getAssetFolderPath(path)}${OVERLAY_SETTINGS_DRAFT_JSON}`
}

